/**
 * Transform Redux data into GeoJSON format for the map
 * @param {Array} allThePoints - Data from Redux store
 * @returns {Object} GeoJSON object with enriched features
 */
export const transformReduxDataToGeoJSON = (allThePoints) => {
  if (!allThePoints || !Array.isArray(allThePoints)) {
    console.warn("No valid data provided to transformReduxDataToGeoJSON", allThePoints);
    return {
      type: "FeatureCollection",
      features: []
    };
  }

  console.log(`Transforming ${allThePoints.length} points from Redux data`);

  // Import settlement types data
  const settlementTypes = require('../assets/allThePoints/settlementTypes.json');

  const features = allThePoints.map((point, index) => {
    try {
      // Handle different possible data structures
      const pointData = point.properties || point;
      
      // Extract coordinates
      const latitude = parseFloat(pointData.Latitude);
      const longitude = parseFloat(pointData.Longitude);
      
      // Skip points without valid coordinates
      if (isNaN(latitude) || isNaN(longitude)) {
        console.warn(`Skipping point ${index} (${pointData.Name || 'unnamed'}) - invalid coordinates: ${pointData.Latitude}, ${pointData.Longitude}`);
        return null;
      }

      // Get settlement classification
      const settlementClass = pointData.SettlementClassification || "";
      const settlementType = settlementTypes[settlementClass];
      
      // Get group classification from settlement type
      const groupClassification = settlementType ? settlementType.group : settlementClass.charAt(0);
      
      // Generate type info from settlement types data
      const typeInfo = generateTypeInfo(settlementClass, settlementType);
      const advIconId = typeInfo ? typeInfo.icon : "default.png";
      const basicIconId = typeInfo ? generateGroupInfo(groupClassification, settlementType)?.icon || "default.png" : "default.png";

      // Parse dates
      const creationDate = pointData.CreationDate;
      const endDate = pointData.EndDate;
      const creationYear = creationDate ? Number(creationDate.split("-")[0]) : null;

      // Replace end date if it includes 2050
      const processedEndDate = endDate && endDate.includes("2050") ? "2023-10-01" : endDate;
      const processedEndYear = processedEndDate ? Number(processedEndDate.split("-")[0]) : null;

      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [longitude, latitude]
        },
        properties: {
          ...pointData,
          groupClassification,
          basicIconId,
          advIconId,
          shape: typeInfo ? typeInfo.shape : 'circle',
          color: typeInfo ? typeInfo.color : '#CCCCCC',
          creationYear,
          endYear: processedEndYear,
          EndDate: processedEndDate,
          // Add any additional properties needed for filtering
          id: pointData.ID || index.toString()
        }
      };
    } catch (error) {
      console.error(`Error processing point ${index}:`, error, point);
      return null;
    }
  }).filter(feature => feature !== null); // Remove null features

  console.log(`Successfully transformed ${features.length} features`);

  return {
    type: "FeatureCollection",
    features
  };
};

// Generate a color based on settlement type color
const getColorForGroup = (colorName) => {
  const colorMapping = {
    green: "#48752C",
    maroon: "#7A2917", 
    purple: "#8C1AF5",
    pink: "#EA33F7",
    red: "#B83F29",
    black: "#000000",
    orange: "#F19E38",
    yellow: "#FFFF54",
    blue: "#5885E1",
    blue_green: "#4ECDC4",
    dark_blue: "#45B7D1",
    grey: "#CCCCCC",
    red_black: "#8B0000"
  };
  
  return colorMapping[colorName] || "#CCCCCC";
};

// Generate group info dynamically
const generateGroupInfo = (groupName, settlementType) => {
  if (!groupName) return null;
  
  return {
    name: groupName,
    color: settlementType ? getColorForGroup(settlementType.color) : "#CCCCCC",
    shape: settlementType ? settlementType.shape : 'circle',
    icon: settlementType ? settlementType.icon : 'default.png'
  };
};

// Generate type info dynamically
const generateTypeInfo = (settlementClass, settlementType) => {
  if (!settlementClass) return null;
  
  if (settlementType) {
    return {
      group: settlementType.group,
      type: settlementType.type,
      color: getColorForGroup(settlementType.color),
      shape: settlementType.shape,
      icon: settlementType.icon
    };
  }
  
  // Fallback for unknown settlement classes
  const groupCode = settlementClass.charAt(0);
  return {
    group: `Group ${groupCode}`,
    type: `Type ${settlementClass}`,
    color: "#CCCCCC",
    shape: 'circle',
    icon: 'default.png'
  };
};

/**
 * Get community groups data for legend
 * @returns {Object} Community groups configuration
 */
export const getCommunityGroups = () => {
  return {};
};

/**
 * Get community types data for advanced mode
 * @returns {Object} Community types configuration
 */
export const getCommunityTypes = () => {
  return {};
};

/**
 * Get settlement types data
 * @returns {Object} Settlement types configuration
 */
export const getSettlementTypes = () => {
  return {};
}; 